<script setup lang="ts">
import { Button, Panel } from "primevue"
import { useCheckUserRights } from '@/composable/useCheckUserRights';
import { computed, ref, watch } from 'vue';
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings';
import { useCreateSwapOrderSettings } from '@/composable/settings/useCreateSwapOrderSettings';
import { useCexCexTwoLegsSettings } from '@/composable/settings/useCexCexTwoLegsSettings';
// import MinTradeVolumeButton from '@/components/dex-cex/MinTradeVolumeButton.vue';
// import AutoSlTpButton from '@/components/dex-cex/AutoSlTpButton.vue';
// import SlTpTriggerButton from '@/components/dex-cex/SlTpTriggerButton.vue';
import AverageSpreadButton from '@/components/dex-cex/AverageSpreadButton.vue';
import TriggerButton from '@/components/TriggerButton.vue';
import type { PositionSide } from "@/composable/useCexPositionsSide";
import type { FormInstance } from "@primevue/forms";

interface FormMarketData {
  "cex": string,
  "asset": string,
  "dexPrice": number | null,
  "cexPrice": number | null,
  "cexPriceBuy": number | null,
  "cexPriceSell": number | null,
  "spread": number | null,
  "backSpread": number | null,
}

interface CexLegSpecificData {
  "isProfilesPrepared": boolean,
  "preparingProfile": boolean,
  "creatingOrder": boolean,
  "fetchingBalance": boolean,
  "enabledProfiles": number[],
}

const props = defineProps<{
  marketData: FormMarketData,
  formData: CexLegSpecificData,
  cacheKeyPostfix: string,
  positionsSide: PositionSide,
  minimized: boolean,
  settingsForm: FormInstance | null
}>()

const emit = defineEmits(["prepare-profile", "show-positions-modal", "fetch-balance", "open-cex-position", "close-cex-position"])
const cacheKeyPostfix = computed(() => props.cacheKeyPostfix)
const { trailingSl, trailingExit, trailingSide, trailingNext, trailingActive, cleanTrailingSl } = useTrailingSlSettings(cacheKeyPostfix)
const { autoPrepare } = useCexCexTwoLegsSettings()

const settingsForm = computed(() => props.settingsForm)
const {
  leverage,
  amount,
  jumpRequired
} = useCreateSwapOrderSettings(settingsForm, cacheKeyPostfix, "dex")

const { isDexCexFree } = useCheckUserRights()
const disablePrepare = computed(() => autoPrepare.value ? props.formData.isProfilesPrepared : false)
const advancedCexCollapsed = ref(true)
const cexPrice = computed(() => {
  if (props.marketData.spread && props.marketData.backSpread) {
    if (props.marketData.spread > props.marketData.backSpread) {
      return props.marketData.cexPriceBuy
    } else {
      return props.marketData.cexPriceSell
    }
  }
  return null
})


watch(
  [
    () => props.marketData.cexPrice,
    () => props.marketData.dexPrice,
  ],
  () => {
    moveTslIfRequired()
    checkIsTslBounded()
    checkIsSpreadClosed()
  },
)

// Логика работы трейлинг-стоп-лосса
const moveTslIfRequired = () => {
  const cexPrice = props.marketData.cexPrice
  const dexPrice = props.marketData.dexPrice

  if (!trailingSl.value || !trailingExit.value || !trailingNext.value || !cexPrice || !dexPrice) {
    return
  }
  const isCexReachSl = (
    (trailingSide.value == "long" && cexPrice >= trailingExit.value)
    ||
    (trailingSide.value == "short" && cexPrice <= trailingExit.value)
  )
  const isDexReachTp = (
    (trailingSide.value == "long" && dexPrice >= trailingNext.value)
    ||
    (trailingSide.value == "short" && dexPrice <= trailingNext.value)
  )
  if (trailingActive.value && isDexReachTp || !trailingActive.value && isCexReachSl) {
    console.log(`trailingActive.value=${trailingActive.value}`)
    console.log(`isDexReachTp=${isDexReachTp}`)
    console.log(`isCexReachSl=${isCexReachSl}`)
    const rightPrice = dexPrice
    trailingActive.value = true
    const stepRatio = 1
    const priceRatio = rightPrice * stepRatio / 100
    if (trailingSide.value == "long") {
      trailingExit.value = rightPrice - priceRatio
      trailingNext.value = rightPrice + priceRatio
    } else {
      trailingExit.value = rightPrice + priceRatio
      trailingNext.value = rightPrice - priceRatio
    }
    console.log(`trailingNext=${trailingNext.value}`)
    console.log(`trailingExit=${trailingExit.value}`)
  }
}

const checkIsTslBounded = () => {
  const dexPrice = props.marketData.dexPrice
  if (!trailingSl.value || !trailingExit.value || !dexPrice) {
    return
  }
  const isCex2ReachSl = (
    (trailingSide.value == "long" && dexPrice <= trailingExit.value)
    ||
    (trailingSide.value == "short" && dexPrice >= trailingExit.value)
  )
  if (isCex2ReachSl) {
    cleanTrailingSl()
    emit('close-cex-position')
  }
}

const MIN_SPREAD_CLOSE = 0.5

const checkIsSpreadClosed = () => {
  if (!trailingSl.value || !trailingExit.value) {
    return
  }
  const isSpreadClosed = (
    (trailingSide.value == "long" && props.marketData.spread !== null && props.marketData.spread < MIN_SPREAD_CLOSE)
    ||
    (trailingSide.value == "short" && props.marketData.backSpread !== null && props.marketData.backSpread < MIN_SPREAD_CLOSE)
  )
  if (isSpreadClosed) {
    cleanTrailingSl()
    emit('close-cex-position')
  }
}

</script>

<template>

  <div v-show="minimized">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <!-- Быстрое закрытие -->
      <Button severity="danger" class="col-span-1" :disabled="!Boolean(cexPrice) ||
        !leverage ||
        !amount ||
        formData.creatingOrder ||
        formData.enabledProfiles.length == 0
        " @click="emit('close-cex-position')" variant="outlined" :loading="formData.creatingOrder" icon="pi pi-bolt"
        :label="$t('modal.createSwapOrders.quickPosClose')" fluid>
      </Button>
      <!-- Быстрое открытие -->
      <Button severity="success" class="col-span-1" :disabled="!Boolean(cexPrice) ||
        !leverage ||
        !amount ||
        formData.preparingProfile ||
        formData.creatingOrder ||
        formData.enabledProfiles.length == 0" type="submit" variant="outlined" :loading="formData.creatingOrder"
        icon="pi pi-bolt" :label="$t('quickOpen')" fluid @click="emit('open-cex-position', true)">
      </Button>
    </div>
  </div>

  <div v-show="!minimized">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-3 col-span-2">

        <!-- Подготовка профиля -->
        <div class="grid grid-cols-4 col-span-2">
          <Button @click="autoPrepare = !autoPrepare" class="rounded-r-none border-r-0" severity="success"
            variant="outlined">
            <i class="pi" :class="{ 'pi-check-square': autoPrepare, 'pi-stop': !autoPrepare }"></i>
            <span>{{ $t("modal.createSwapOrders.auto") }}</span>
          </Button>
          <Button severity="success" class="col-span-3 rounded-l-none" variant="outlined"
            :disabled="!leverage || formData.preparingProfile || formData.enabledProfiles.length == 0 || disablePrepare"
            @click="emit('prepare-profile')" :loading="formData.preparingProfile"
            :label="$t('modal.createSwapOrders.prepareProfile')">
          </Button>
        </div>

        <!-- Быстрое открытие -->
        <Button severity="success" class="col-span-2 lg:col-span-2" :disabled="!Boolean(cexPrice) ||
          !leverage ||
          !amount ||
          formData.preparingProfile ||
          formData.creatingOrder ||
          formData.enabledProfiles.length == 0" type="submit" variant="outlined" :loading="formData.creatingOrder"
          icon="pi pi-bolt" :label="$t('quickOpen')" fluid @click="emit('open-cex-position', true)">
        </Button>
      </div>

      <!-- Быстрое закрытие -->
      <Button severity="danger" class="col-span-2 lg:col-span-1" :disabled="!Boolean(cexPrice) ||
        !leverage ||
        !amount ||
        formData.creatingOrder ||
        formData.enabledProfiles.length == 0
        " @click="emit('close-cex-position')" variant="outlined" :loading="formData.creatingOrder" icon="pi pi-bolt"
        :label="$t('modal.createSwapOrders.quickPosClose')" fluid>
      </Button>

      <template v-if="!isDexCexFree">
        <!-- Триггер -->
        <TriggerButton class="col-span-2 lg:col-span-1" :root="cacheKeyPostfix" cache-key="cex" :amount="amount">
        </TriggerButton>
      </template>

      <template v-if="!isDexCexFree">
        <!-- Мои позиции -->
        <Button fluid class="col-span-2 lg:col-span-1" severity="success" variant="outlined" icon="pi pi-shopping-cart"
          @click="emit('show-positions-modal')" :label="$t('modal.createSwapOrders.myPositions')">
        </Button>

        <!-- Узнать баланс -->
        <Button fluid icon="pi pi-money-bill" severity="success" :loading="formData.fetchingBalance"
          class="col-span-2 lg:col-span-1" :disabled="formData.enabledProfiles.length == 0 || formData.fetchingBalance"
          @click="emit('fetch-balance')" variant="outlined" :label="$t('modal.createSwapOrders.fetchBalance')">
        </Button>
      </template>
    </div>

    <Panel toggleable :collapsed="advancedCexCollapsed" class="mt-3">
      <template #header>
        {{ $t('advancedCex') }}
      </template>
      <div class="grid grid-cols-2 gap-2">

        <!-- Ср спред -->
        <AverageSpreadButton class="col-span-2" :root="cacheKeyPostfix" cache-key="dex-cex">
        </AverageSpreadButton>

        <!-- Стоп-лосс и тейк-профит -->
        <!-- <SlTpTriggerButton class="col-span-2 lg:col-span-1" :positionsSide="positionsSide" ref="closingTriggerButtonDex"
          severity="success" variant="outlined" :root="cacheKeyPostfix"
          :label="$t('modal.createSwapOrders.closingConditions')">
        </SlTpTriggerButton> -->

        <!-- Автозакрытие -->
        <!-- <AutoSlTpButton class="col-span-2 lg:col-span-1" ref="autoSltpButtonDex" :root="cacheKeyPostfix"
          severity="success" variant="outlined" :label="$t('modal.createSwapOrders.autoSlTp')">
        </AutoSlTpButton> -->

        <!-- Скользящий стоп-лосс -->
        <Button fluid class="col-span-2 lg:col-span-1" severity="success" variant="outlined"
          @click="trailingSl = !trailingSl" :icon="trailingSl ? 'pi pi-check-square' : 'pi pi-stop'"
          :label="$t('modal.createSwapOrders.trailingSl')">
        </Button>

        <!-- Резкие движения -->
        <Button :icon="jumpRequired ? 'pi pi-check-square' : 'pi pi-stop'" fluid class="col-span-2 lg:col-span-1"
          @click="jumpRequired = !jumpRequired" variant="outlined" severity="success" :label="$t('waitDexJump')" />

        <!-- Min Trade -->
        <!-- <MinTradeVolumeButton class="col-span-2 lg:col-span-1" :root="cacheKeyPostfix" :amount="amount">
        </MinTradeVolumeButton> -->
      </div>
    </Panel>
  </div>
</template>
